import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { menuGroups } from '../../pages/data/menuConfig';
import { cn } from '../../lib/utils';
import { Icon } from '@/components/ui/icon';
import { ChevronDown } from 'lucide-react';

const HorizontalNav = () => {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);

  const isLinkActive = (item) => {
    if (location.pathname === item.path) return true;
    if (item.sub) {
      return item.sub.some(subItem => {
        if (location.pathname === subItem.path) return true;
        if (subItem.sub) {
          return subItem.sub.some(s => location.pathname === s.path);
        }
        return false;
      });
    }
    return false;
  };

  return (
    <div className="hidden lg:block w-full border-b border-border bg-card/60 backdrop-blur-md sticky top-16 z-20 select-none">
      <div className={cn(
        "max-w-[1800px] mx-auto px-6 h-12 flex items-center transition-all",
        activeDropdown ? "overflow-visible" : "overflow-x-auto scrollbar-none"
      )}>
        <nav className="flex items-center gap-1 whitespace-nowrap">
          {menuGroups.map((group, gIdx) => (
            <div key={group.label} className="flex items-center gap-1">
              {gIdx > 0 && <span className="h-4 w-px bg-border/60 mx-2" />}
              {group.items.map((item, idx) => {
                const isActive = isLinkActive(item);
                const hasSub = !!item.sub;

                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => hasSub && setActiveDropdown(item.label)}
                    onMouseLeave={() => hasSub && setActiveDropdown(null)}
                  >
                    {hasSub ? (
                      <button
                        className={cn(
                          "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all duration-200",
                          isActive
                            ? "bg-primary/10 text-primary font-semibold"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        <Icon icon={item.icon} size="sm" className="shrink-0" />
                        <span>{item.label}</span>
                        <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", activeDropdown === item.label && "rotate-180")} />
                      </button>
                    ) : (
                      <NavLink
                        to={item.path}
                        className={({ isActive: linkActive }) =>
                          cn(
                            "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all duration-200",
                            linkActive
                              ? "bg-primary/10 text-primary font-semibold"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )
                        }
                      >
                        <Icon icon={item.icon} size="sm" className="shrink-0" />
                        <span>{item.label}</span>
                      </NavLink>
                    )}

                    {/* Submenu Dropdown */}
                    <AnimatePresence>
                      {hasSub && activeDropdown === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.15, ease: 'easeOut' }}
                          className="absolute left-0 top-full pt-1.5 w-60 z-30"
                        >
                          <div className="rounded-xl border border-border bg-popover text-popover-foreground shadow-lg p-1.5 space-y-0.5 backdrop-blur-md bg-popover/90">
                            {item.sub.map((subItem) => {
                              const isSubActive = location.pathname === subItem.path ||
                                (subItem.sub && subItem.sub.some(s => location.pathname === s.path));
                              const hasDoubleSub = !!subItem.sub;

                              return (
                                <div key={subItem.label} className="relative group/sub">
                                  {hasDoubleSub ? (
                                    <div
                                      className={cn(
                                        "flex items-center justify-between w-full text-left text-xs px-3 py-2 rounded-lg cursor-pointer transition-colors",
                                        isSubActive
                                          ? "bg-primary/10 text-primary font-medium"
                                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                      )}
                                    >
                                      <span>{subItem.label}</span>
                                      <Icon icon={ChevronDown} size="xs" className="-rotate-90" />
                                    </div>
                                  ) : (
                                    <NavLink
                                      to={subItem.path}
                                      className={({ isActive: subLinkActive }) =>
                                        cn(
                                          "flex items-center text-xs px-3 py-2 rounded-lg transition-colors",
                                          subLinkActive
                                            ? "bg-primary/10 text-primary font-medium"
                                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                        )
                                      }
                                    >
                                      <span>{subItem.label}</span>
                                    </NavLink>
                                  )}

                                  {/* Double Submenu level 3 */}
                                  {hasDoubleSub && (
                                    <div className="hidden group-hover/sub:block absolute left-full top-0 ps-1.5 w-48">
                                      <div className="rounded-lg border border-border bg-popover shadow-md p-1 space-y-0.5">
                                        {subItem.sub.map((deepItem) => (
                                          <NavLink
                                            key={deepItem.label}
                                            to={deepItem.path}
                                            className={({ isActive: deepActive }) =>
                                              cn(
                                                "flex items-center text-[11px] px-2.5 py-1.5 rounded transition-colors",
                                                deepActive
                                                  ? "bg-primary/10 text-primary font-medium"
                                                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                              )
                                            }
                                          >
                                            {deepItem.label}
                                          </NavLink>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default HorizontalNav;
